<script>
import FileBrowserUpload from "@/components/FileBrowserUpload.vue";
import FileBrowserFolderCreate from "@/components/FileBrowserFolderCreate.vue";
import FileBrowserFolderEdit from "@/components/FileBrowserFolderEdit.vue";
import fileHelper from "@/mixins/fileHelper.js";
import apiHelper from "@/mixins/apiHelper.js";

export default {
  components: {
    FileBrowserUpload,
    FileBrowserFolderCreate,
    FileBrowserFolderEdit: FileBrowserFolderEdit,
  },
  mixins: [
    fileHelper,
    apiHelper,
  ],
  emits: ['onFileSelect'],
  props: {
    fileSelect: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  async mounted() {
    // Load root folder
    await this.openFolder();
  },
  data() {
    return {
      loading: true,
      folderParentUuid: null,
      folderId: null,
      selectedItems: [],
      folder: {},
    }
  },
  computed: {
    items() {
      return this.folder.items;
    },
  },
  methods: {
    rowDoubleClick(row) {
      if (row.data.type === 'folder') {
        this.openFolder(row.data.id)
      }
    },
    openParentFolder() {
      this.openFolder(this.folderParentUuid);
    },
    async openFolder(folder_id) {
      this.loading = true;
      if (folder_id === undefined || folder_id === null) {
        folder_id = 'root';
      }
      // Fetch session token
      const response = await fetch('/api/file-folder/' + folder_id, {
        credentials: "include"
      })
      this.loading = false;
      const status_code = response.status;
      if (status_code !== 200) {
        // Show toast
        this.$toast.add({ severity: 'error', summary: 'Fout', detail: 'Error: Kon de map niet laden.', life: 3000 });
      }
      // Parse the response
      let data = await response.json();
      // Set folder and file items
      this.folder = data;
      // Set the parent folder id
      if (folder_id === 'root') {
        this.folderParentUuid = null;
        this.folderId = null;
      } else {
        this.folderParentUuid = data.parent_id;
        this.folderId = folder_id;
      }
    },
    openUploadDialog() {
      this.$refs.fileBrowserUpload.openDialog();
    },
    openFolderCreateDialog() {
      this.$refs.folderCreate.openDialog();
    },
    uploadSuccess(files) {
      this.reloadFolder();
    },
    folderCreated() {
      this.reloadFolder();
    },
    reloadFolder() {
      this.openFolder(this.folderId);
    },
    selectFiles() {
      this.$emit('onFileSelect', this.selectedItems);
    },
    confirmDeleteItem(data) {
      let type = data.type === 'folder' ? 'de map' : 'het bestand';

      this.$confirm.require({
        message: 'Weet u zeker dat u ' + type + ' ' + data.name + ' wilt verwijderen?',
        header: 'Waarschuwing',
        rejectProps: {
          label: 'Annuleren',
          severity: 'secondary',
          outlined: true
        },
        acceptProps: {
          label: 'Verwijder',
          severity: 'danger'
        },
        accept: () => {
          if (data.type === 'folder') {
            this.deleteFolder(data.id);
          } else {
            this.deleteFile(this.folderId, data.id);
          }
        },
        reject: () => {

        }
      });
    },
    async deleteFolder(folder_id) {
      // Remove the folder
      const response = await fetch('/api/file-folder/' + folder_id, {
        method: 'DELETE',
        credentials: "include",
        headers: this.requestHeaders,
      })
      const status_code = response.status;
      if (status_code !== 204) {
        // Show error toast
        this.$toast.add({ severity: 'error', summary: 'Fout', detail: 'Error: Kon de map niet verwijderen.' });
        return;
      }
      // Show success toast
      this.$toast.add({ severity: 'success', summary: 'Geslaagd', detail: 'Map is verwijderd', life: 3000 });
      // Reload the folder
      this.reloadFolder();
    },
    async deleteFile(folder_id, file_id) {
      // Remove the folder
      const response = await fetch('/api/file-folder/' + folder_id + '/delete-file/' + file_id, {
        method: 'DELETE',
        credentials: "include",
        headers: this.requestHeaders,
      })
      const status_code = response.status;
      if (status_code !== 204) {
        // Show error toast
        this.$toast.add({severity: 'error', summary: 'Fout', detail: 'Error: Kon het bestand niet verwijderen.'});
        return;
      }
      // Show success toast
      this.$toast.add({severity: 'success', summary: 'Geslaagd', detail: 'Bestand is verwijderd', life: 3000});
      // Reload the folder
      this.reloadFolder();
    },
    openFolderEditDialog(data) {
      this.$refs.folderEdit.openDialog(data);
    },
    folderEditSuccess() {
      this.reloadFolder();
    }
  }
}
</script>

<template>

  <Toast position="top-center" />

  <ConfirmDialog></ConfirmDialog>

  <!-- selectAll: Is disabled for now, because then we need extra logic when selecting all files. For now only use the single select emit onRowSelect/onRowUnselect -->
  <DataTable
    v-model:selection="selectedItems"
    :value="items"
    size="small"
    scrollable
    scrollHeight="flex"
    dataKey="id"
    tableStyle="min-width: 50rem"
    sortField="name"
    :sortOrder="1"
    :selectAll="false"
    @row-dblclick="rowDoubleClick"
  >
    <template #header>
      <Toolbar>
        <template #start>
          <Button v-if="folderParentUuid" label="Terug" outlined @click="openParentFolder" />
        </template>
        <template #center>
          <span class="text-xl font-bold"><i class="fas fa-folder-open"></i> {{ folder.name }}</span>
        </template>
        <template #end>
          <Button label="Nieuwe map" class="mr-2" outlined @click="openFolderCreateDialog" />

          <Button v-if="folderId" label="Upload" class="mr-2" @click="openUploadDialog" />
          <Button v-if="!folderId" :disabled="true" label="Upload" class="mr-2" v-tooltip.top="'In de hoofdmap kunt u alleen mappen aanmaken.'" />

        </template>
      </Toolbar>
      <div style="height: 6px" class="loader">
        <ProgressBar mode="indeterminate" style="height: 6px" v-if="loading"></ProgressBar>
      </div>
    </template>

    <template #empty>
      <span v-if="loading">Map wordt geladen..</span>
      <span v-else>Lege map</span>
    </template>

    <Column
      v-if="fileSelect"
      selectionMode="multiple"
      headerStyle="width: 3rem"
      class="hide-select-all-checkbox"
    >
      <template #body="{ data }">
        <Checkbox v-if="data.type === 'file'" v-model="selectedItems" :value="data" />
      </template>
    </Column>
    <Column field="name" header="Naam" sortable>
      <template #body="{ data }">
        <i class="fas fa-folder" v-if="data.type === 'folder'"></i>
        <i class="far fa-file" v-if="data.type === 'file'"></i>

        <a v-if="data.type === 'file'" target="_blank" :href="data.url" class="ml-1">{{ data.name }}</a>
        <span v-if="data.type === 'folder'" class="ml-1">
          {{ data.name }}
          <Chip v-if="data.shared === true" label="Gedeeld"></Chip>
        </span>

      </template>
    </Column>
    <Column field="size" header="Grootte" sortable>
      <template #body="{ data }">
        <span v-if="data.type === 'folder'">-</span>
        <span v-if="data.type === 'file'">{{ formatSize(data.size) }}</span>
      </template>
    </Column>
    <Column field="mime_type" header="Type" sortable>
      <template #body="{ data }">
        <span v-if="data.type === 'folder'">Map</span>
        <span v-if="data.type === 'file'">{{ formatMimeType(data.mime_type) }}</span>
      </template>
    </Column>
    <Column field="user" header="Aangemaakt door" sortable>
      <template #body="{ data }">
        <span>{{ data.user }}</span>
      </template>
    </Column>
    <Column :exportable="false" style="min-width: 12rem" bodyStyle="text-align:right">
      <template #body="slotProps">
        <Button v-if="slotProps.data.type === 'folder'" size="small" text rounded class="mr-2" severity="secondary" @click="openFolderEditDialog(slotProps.data)" v-tooltip.top="{ value: 'Aanpassen', showDelay: 300 }">
          <template #icon><i class="far fa-pencil"></i></template>
        </Button>
        <Button size="small" text rounded severity="secondary" @click="confirmDeleteItem(slotProps.data)" v-tooltip.top="{ value: 'Verwijderen', showDelay: 300 }">
          <template #icon><i class="far fa-trash-alt"></i></template>
        </Button>
      </template>
    </Column>

    <template #footer v-if="fileSelect">
      <p>
        <template v-if="selectedItems.length === 1">1 bestand geselecteerd.</template>
        <template v-else>{{ selectedItems.length }} bestanden geselecteerd.</template>
      </p>
      <Button :disabled="selectedItems.length === 0" label="Selecteer bestanden" @click="selectFiles" />
    </template>
  </DataTable>

  <FileBrowserUpload :file-upload-url="'/api/folder-file-upload/' + folderId" ref="fileBrowserUpload" @upload-success="uploadSuccess"></FileBrowserUpload>

  <FileBrowserFolderCreate :folderUuid="folderId" ref="folderCreate" @folder-created="folderCreated"></FileBrowserFolderCreate>

  <FileBrowserFolderEdit ref="folderEdit" @edit-success="folderEditSuccess"></FileBrowserFolderEdit>

</template>

<style>
  th.hide-select-all-checkbox {
    .p-checkbox {
      display: none;
    }
  }
  .p-datatable-table .p-button-sm {
    padding: 0.3rem 0.5rem;
  }
</style>
